import { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setActiveItems } from '../redux/slices/clothingItemsSlice';
import { setActivePersonImage } from '../redux/slices/personImageSlice';
import { selectVTOResult as selectVTOResultAction, updateVTOSession } from '../redux/slices/vtoSlice';

/**
 * Hook for managing lookbook panel state and handlers
 * Extracted from MyCloset.js to reduce file size
 */
const useLookbookPanel = ({
    layout,
    actualClothingItems,
    personImagesData,
    personImageState
}) => {
    const dispatch = useDispatch();

    // Panel view state (shows closets/explore/lookbooks instead of clothing grid)
    const [showPanelView, setShowPanelView] = useState(false);
    
    // Check URL parameters for initial view
    const initialView = new URLSearchParams(window.location.search).get('view') === 'lookbook' ? 'lookbook' : 'closet';
    console.log('✅ [susu_closet] useLookbookPanel initialView:', initialView, 'from search:', window.location.search);
    const [panelViewType, setPanelViewType] = useState(initialView); // 'closet' or 'lookbook'
    const [closetPanelState, setClosetPanelState] = useState(null);
    const [lookbookPanelState, setLookbookPanelState] = useState(null);
    const [selectedClosetId, setSelectedClosetId] = useState(null);
    const [selectedClosetName, setSelectedClosetName] = useState(null);
    const [selectedLookbookId, setSelectedLookbookId] = useState(null); // null = default lookbook
    const [selectedLookbookName, setSelectedLookbookName] = useState('My Lookbook');

    // Handler for opening lookbook panel from VTO bottom panel
    const handleOpenLookbookPanel = useCallback(() => {
        setPanelViewType('lookbook');
        // Default to "My Lookbook" view (Items View)
        setShowPanelView(false);
        setSelectedLookbookId(null); // null means default lookbook
        setSelectedLookbookName('My Lookbook');

        // Ensure panel is expanded
        if (layout && layout.handleCollapseToggle) {
            layout.handleCollapseToggle(false);
        }
    }, [layout]);

    // Listen for postMessage to open lookbook from parent window
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data && event.data.type === 'OPEN_LOOKBOOK') {
                console.log('✅ [susu_closet] Received OPEN_LOOKBOOK message! Calling handleOpenLookbookPanel...');
                handleOpenLookbookPanel();
                if (event.source && event.source.postMessage) {
                    event.source.postMessage({ type: 'LOOKBOOK_OPENED' }, '*');
                }
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [handleOpenLookbookPanel]);

    // Handler for going back to closets/folders from lookbook panel
    const handleBackToLookbooks = useCallback(() => {
        // If we are in lookbook mode and in items view, go back to folder view
        if (panelViewType === 'lookbook' && !showPanelView) {
            setShowPanelView(true); // Switch to Folder View
        } else if (lookbookPanelState?.onBack) {
            // Fallback to internal state handler if needed
            lookbookPanelState.onBack();
        }
    }, [panelViewType, showPanelView, lookbookPanelState]);

    // Determine current panel view name for header
    const currentPanelViewName = panelViewType === 'lookbook'
        ? (showPanelView ? 'Lookbooks' : (selectedLookbookName || 'My Lookbook'))
        : selectedClosetName;

    // Handler for selecting an entry in the lookbook panel (same behavior as FitHistorySection)
    const handleLookbookEntrySelect = useCallback((entry) => {
        console.log('[Lookbook] Selecting entry:', entry);

        // Extract clothing items from the VTO result
        let clothingItems = [];
        const metadataLayeredItems = entry.generationMetadata?.layeredItems || entry.metadata?.layeredItems;

        if (metadataLayeredItems && Array.isArray(metadataLayeredItems) && metadataLayeredItems.length > 0) {
            clothingItems = metadataLayeredItems
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(metadataItem => {
                    const fullItem = actualClothingItems.find(item => item.id === metadataItem.id);
                    if (fullItem) {
                        return {
                            ...fullItem,
                            id: fullItem.id,
                            name: fullItem.clothesName || fullItem.name,
                            clothesName: fullItem.clothesName,
                            category: metadataItem.category || fullItem.category,
                            image: fullItem.image || fullItem.coverImage,
                            imageUrl: fullItem.image?.url || fullItem.coverImage?.url || fullItem.imageUrl
                        };
                    }
                    return null;
                })
                .filter(item => item != null);
        } else if (entry.clothingItems) {
            clothingItems = Array.isArray(entry.clothingItems) ? entry.clothingItems : [entry.clothingItems];
        } else if (entry.closetClothes) {
            clothingItems = Array.isArray(entry.closetClothes) ? entry.closetClothes : [entry.closetClothes];
        }

        clothingItems = clothingItems.filter(item => item != null).flat();

        // Update active person image
        const personId = entry.personImage?.id || entry.personImageId || entry.person_image?.id;
        if (personId) {
            dispatch(setActivePersonImage({ personImageId: personId }));
            dispatch(updateVTOSession({ personImageId: personId }));
        }

        // Set active items (clothing worn in this result)
        dispatch(setActiveItems(clothingItems));

        // Load VTO result image onto canvas
        if (entry.imageUrl || (Array.isArray(entry.resultImage) ? entry.resultImage[0]?.url : entry.resultImage?.url)) {
            const shouldUseRemoved = entry.useRemovedImage !== false && entry.removedBgImage?.url;
            const vtoImageUrl = shouldUseRemoved ? (Array.isArray(entry.removedBgImage) ? entry.removedBgImage[0]?.url : entry.removedBgImage?.url) : (entry.imageUrl || (Array.isArray(entry.resultImage) ? entry.resultImage[0]?.url : entry.resultImage?.url));

            // Get full person image data for revert fallback
            const fullPersonImageData = personImagesData.personImages?.find(img => img.id === personId);
            const personImageUseRemovedFlag = fullPersonImageData?.useRemovedImage !== false;
            let originalPersonImageUrl;

            if (personImageUseRemovedFlag && fullPersonImageData?.removedBgImage?.url) {
                originalPersonImageUrl = fullPersonImageData.removedBgImage.url;
            } else if (fullPersonImageData?.image?.url) {
                originalPersonImageUrl = fullPersonImageData.image.url;
            } else {
                originalPersonImageUrl = entry.personImageUrl || entry.personImage?.url || entry.person_image?.url || fullPersonImageData?.url || fullPersonImageData?.image?.url;
            }

            personImageState.setVirtualTryOnResult({
                image: vtoImageUrl,
                originalImage: originalPersonImageUrl,
                metadata: {
                    isVirtualTryOnResult: true,
                    originalPersonImage: originalPersonImageUrl,
                    garmentId: entry.closetClothes?.id,
                    garmentName: entry.closetClothes?.clothesName,
                    timestamp: entry.created_at || entry.createdAt,
                    vtoResultId: entry.id,
                    watermarkedImage: Array.isArray(entry.watermarkedImage) ? entry.watermarkedImage[0]?.url : entry.watermarkedImage?.url
                }
            });

            personImageState.setCanvasIntegrationMode(true);
        }

        // Select the VTO result in Redux (this will highlight it in LOOKS column too)
        dispatch(selectVTOResultAction(entry.id));
        console.log('[Lookbook] Selected VTO result:', entry.id);
    }, [dispatch, actualClothingItems, personImagesData.personImages, personImageState]);

    // Handler for selecting a closet from the closet panel
    const handleClosetSelect = useCallback((closetId, closetName, isDefault) => {
        setSelectedClosetId(closetId);
        setSelectedClosetName(closetName);
        setShowPanelView(false);
    }, []);

    // Handler for selecting a lookbook from the lookbook panel
    const handleLookbookSelect = useCallback((lookbookId, lookbookName, isDefault) => {
        console.log('Selected lookbook:', lookbookId, lookbookName, 'isDefault:', isDefault);
        // For default lookbook, set ID to null so LookbookItemsPanel uses VTO results
        setSelectedLookbookId(isDefault ? null : lookbookId);
        setSelectedLookbookName(lookbookName);
        setShowPanelView(false); // Switch to Items View
    }, []);

    // Handler for closing lookbook panel
    const handleCloseLookbookPanel = useCallback(() => {
        setShowPanelView(false);
        setPanelViewType('closet');
    }, []);

    // Handler for going back to closets
    const handleBackToClosets = useCallback(() => {
        setSelectedClosetId(null);
        setSelectedClosetName(null);
        setShowPanelView(true);
    }, []);

    return {
        // State
        showPanelView,
        setShowPanelView,
        panelViewType,
        setPanelViewType,
        closetPanelState,
        setClosetPanelState,
        lookbookPanelState,
        setLookbookPanelState,
        selectedClosetId,
        selectedClosetName,
        selectedLookbookId,
        selectedLookbookName,
        currentPanelViewName,

        // Handlers
        handleOpenLookbookPanel,
        handleBackToLookbooks,
        handleLookbookEntrySelect,
        handleClosetSelect,
        handleLookbookSelect,
        handleCloseLookbookPanel,
        handleBackToClosets
    };
};

export default useLookbookPanel;
